/**
 * @fileoverview MainWrapper component for consistent layout
 * @active MAINTAINED - Used in current Next.js App Router routes
 *
 * This component provides a consistent layout structure for pages,
 * with optional custom styles for the outer and inner flex containers.
 *
 */

"use client";

import { type ReactNode } from "react";

import { Flex, type FlexProps } from "@chakra-ui/react";

/**
 * Props interface for MainWrapper component
 * @active Used in current Next.js App Router routes
 * @see src/components/ui/box/MainWrapper.tsx
 */
interface MainWrapperProps {
  /** Child components to render inside the wrapper */
  children: ReactNode;
  /** Custom styles for the outer flex container */
  customStyleOuter?: Partial<FlexProps>;
  /** Custom styles for the inner flex container */
  customStyleInner?: Partial<FlexProps>;
}

/**
 * A wrapper component that provides consistent layout structure
 * @active Used in current Next.js App Router routes
 * @see src/components/ui/box/MainWrapper.tsx
 * @see {@link src/components/companies/Companies.tsx} - DEPRECATED component that uses this
 * @see {@link src/app/(main)/(home)/(research)/companies/page.tsx} - ACTIVE page that uses this
 */
export const MainWrapper = ({
  children,
  customStyleOuter = {},
  customStyleInner = {},
}: MainWrapperProps) => {
  return (
    <Flex
      w={"full"}
      h={"fit-content"}
      flexDir={"column"}
      align={"center"}
      px={{ base: "0px", md: "40px", lg: "40px" }}
      {...customStyleOuter}
    >
      <Flex
        flexDir={"column"}
        w={"full"}
        h={"fit-content"}
        maxW={"1360px"}
        {...customStyleInner}
      >
        {children}
      </Flex>
    </Flex>
  );
};
